import { useState, useMemo, useCallback, useEffect } from 'react'
import { Loader2, Plus, Users } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { useProfile } from '@/hooks/useProfile'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useTeamMembers } from '@/hooks/useTeamMembers'
import { canInviteUsers } from '@dcp/shared'
import { Button, SearchInput, PageHeader } from '@atrvd/saas-ui'
import { SectionHeading } from '@/components/common/SectionHeading'
import { TeamMembersTable } from '@/components/settings/TeamMembersTable'
import { tableQueryState } from '@/components/common/queryErrorDisplay'
import { UserInviteForm } from '@/components/settings/UserInviteForm'
import { useInvitationEmailStatus } from '@/hooks/useInvitationEmailStatus'

/**
 * Team management page under Settings → Team.
 *
 * Displays a single unified table of active members and open/processing/expired
 * invitations (AC9). Org owners and admins can invite new members via the
 * "Invite Member" button which opens the UserInviteForm modal.
 *
 * Permission gating (manage_users) is added in Stage 3a — leave page-level
 * permission checks alone for now.
 */
export function SettingsUsersPage() {
  const [isInviteFormOpen, setIsInviteFormOpen] = useState(false)
  const [memberSearch, setMemberSearch] = useState('')

  /**
   * Tracks the most recently created invitation that is pending email delivery.
   * Set by onInviteCreated (forwarded from UserInviteForm after commit) and
   * cleared once a terminal status (sent / failed / timeout) is reached.
   * Both pieces of state are held here (not in the form) because the form
   * unmounts when the modal closes, which would kill any hook inside it.
   */
  const [pendingInvitationId, setPendingInvitationId] = useState<string | null>(null)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)

  const { data: profile, isLoading: profileLoading } = useProfile()
  const { currentOrganization } = useOrganization()
  const teamMembersQuery = useTeamMembers()
  const teamMembers = teamMembersQuery.data

  // Product Admin (system 'user' + product_role='admin') gains invite rights
  // alongside the legacy CAN_INVITE_ROLES. See canInviteUsers in @dcp/shared.
  const canInvite =
    !!profile?.role &&
    canInviteUsers({ role: profile.role, product_role: profile.productRole })

  /**
   * Poll email delivery status for the most recently created invitation.
   * Polling is active only while pendingInvitationId is non-null.
   * This hook lives at the page level (not inside the form) so it survives
   * after the modal closes.
   */
  const { status: emailStatus } = useInvitationEmailStatus(pendingInvitationId)

  /**
   * React to terminal email delivery status and show the appropriate toast.
   * Clears pendingInvitationId so polling stops and won't re-fire on the
   * next render after the state is reset.
   */
  useEffect(() => {
    if (!pendingInvitationId) return

    if (emailStatus === 'sent') {
      toast.success(`Invitation sent to ${pendingEmail ?? 'the invitee'}`)
      setPendingInvitationId(null)
      setPendingEmail(null)
    } else if (emailStatus === 'failed') {
      toast.error('Email delivery failed. Use Resend to retry.')
      setPendingInvitationId(null)
      setPendingEmail(null)
    } else if (emailStatus === 'timeout') {
      toast.warning(
        'Email is still sending. It may take a few minutes — refresh the team list to check status.'
      )
      setPendingInvitationId(null)
      setPendingEmail(null)
    }
  }, [emailStatus, pendingInvitationId, pendingEmail])

  /**
   * Callback forwarded to UserInviteForm.
   * Called after the invitation row commits and before the modal closes.
   * Stores the new invitation id so email status polling can begin.
   */
  const handleInviteCreated = useCallback((invitationId: string, recipientEmail: string) => {
    setPendingInvitationId(invitationId)
    setPendingEmail(recipientEmail)
  }, [])

  /**
   * Filter the unified team member list by search query.
   * Matches against display name (fullName / firstName+lastName) or email.
   */
  const filteredMembers = useMemo(() => {
    if (!memberSearch) return teamMembers
    const query = memberSearch.toLowerCase()
    return teamMembers.filter((m) => {
      if (m.email.toLowerCase().includes(query)) return true
      // UnifiedTeamMember is a union: `member` rows expose fullName, `invitation`
      // rows expose firstName + lastName separately. Match against whichever the row carries.
      if (m.kind === 'member') {
        return m.fullName?.toLowerCase().includes(query) ?? false
      }
      const composed = [m.firstName, m.lastName].filter(Boolean).join(' ').toLowerCase()
      return composed.includes(query)
    })
  }, [teamMembers, memberSearch])

  // The profile decides whether Invite Member is offered and which row is
  // "(you)", so the page waits for it; the team list loads inside its table.
  if (profileLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Total count shown in the section heading: all rows (members + invitations)
  const totalCount = teamMembers.length

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team"
        description="Manage your organization's team members"
        actions={
          canInvite && (
            <Button onClick={() => setIsInviteFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          )
        }
      />

      {/* Unified team members section — active members and all open invitations.
          A section, not a Card: DataTable draws its own card frame. */}
      <section className="space-y-4">
        <SectionHeading
          icon={Users}
          description={`Users with access to ${currentOrganization?.name || 'this organization'}`}
        >
          Team Members
          {totalCount > 0 && <span className="text-sm font-normal text-muted-foreground">({totalCount})</span>}
        </SectionHeading>
        <SearchInput
          placeholder="Search members by name or email..."
          value={memberSearch}
          onChange={setMemberSearch}
          clearable
          className="max-w-sm"
        />
        <TeamMembersTable
          members={filteredMembers}
          searchTerm={memberSearch}
          currentUserId={profile?.id}
          {...tableQueryState(teamMembersQuery, filteredMembers.length)}
        />
      </section>

      {/* Invite Form Modal */}
      <UserInviteForm
        open={isInviteFormOpen}
        onOpenChange={setIsInviteFormOpen}
        onInviteCreated={handleInviteCreated}
      />
    </div>
  )
}
